/**
 * The 30 NBA clubs: the table every `/nba/*` page joins its markets against.
 *
 * `nbaTeamDirectory.json` is generated — `node scripts/generate-nba-team-directory.mjs`
 * writes it from Kalshi's own structured targets — so the table and the ids the
 * markets carry can't drift. Edit the generator, not the file.
 *
 * Identity only: a club's conference, division, names, logo, palette and page.
 * Deliberately no form — no record, no rank, no standing. Those come from the
 * team-hub live-data lane (`_lib/nbaHub.ts`), which is refreshed every sweep;
 * anything copied into a committed file would be quoted as current long after it
 * stopped being true.
 *
 * ## Why the pages need this at all
 *
 * Less than the MLB pages do. Basketball's standings rows arrive carrying their
 * own `structured_target_id`, so the market join needs no lookup here (see
 * `nbaHub.ts`). What this table supplies is what the feed does *not*: the page
 * slug a club's team page lives at, its logo, its palette key, and a stable
 * league-wide ordering for the pages that list every club.
 */
import nbaTeamDirectoryJson from './nbaTeamDirectory.json';

/** Which conference a club plays in. */
export type NbaConference = 'East' | 'West';

/** The three divisions each conference is split into. */
export type NbaDivisionName = 'Atlantic' | 'Central' | 'Southeast' | 'Northwest' | 'Pacific' | 'Southwest';

/** One club's row in the committed directory: who it is, not how it is doing. */
export interface NbaTeamIdentity {
    /** Kalshi structured-target id — the join key every market family carries. */
    id: string;
    /** City/market half, e.g. "Boston" — how Kalshi titles its markets. */
    market: string;
    /**
     * Nickname alone, e.g. "Celtics".
     *
     * The generator strips this out of `details.team_name`, which arrives
     * club-code prefixed on all thirty rows ("BOS Celtics"). See its header.
     */
    teamName: string;
    /**
     * Full name, e.g. "Boston Celtics".
     *
     * Published rather than composed at the render site, because it is not always
     * `market` + `teamName`: the Clippers' market is "LA" where the Lakers' is
     * "Los Angeles". See the generator's `NAME_OVERRIDES`.
     */
    name: string;
    conference: NbaConference;
    division: NbaDivisionName;
    /**
     * The key this club's copy is filed under in `BASKETBALL_FAQ_CONFIGS` —
     * `boston`, `los-angeles-c`, `golden-state`.
     *
     * **Not a URL**, despite reading like one, and despite the generator having
     * described it as "the `/teams/basketball-team/[name]` slug each team's page
     * is served at". It is not: `/teams/basketball-team/boston` serves *UMass
     * Boston*, `memphis` serves the NCAA Memphis Tigers and `denver` the NCAA
     * Denver Pioneers — those clubs' names really do slugify to those, where an
     * NBA club's name is club-code prefixed and never bare. See `legacySlug`
     * for the URL, and treat this as a lookup key only.
     */
    slug: string;
    /**
     * The `/teams/basketball-team/[name]` segment this club really is served at
     * — `bos-celtics`, `la-lakers`, `gs-warriors`.
     *
     * Slugified from the structured target's own `name` ("BOS Celtics"), which
     * is the same rule `sitemapper.js` generates those URLs with, so the table
     * and the sitemap cannot disagree. This is what redirects to `path`.
     */
    legacySlug: string;
    /**
     * The `/nba/teams/[team]` segment: city and mascot, e.g. `atlanta-hawks`.
     *
     * The canonical URL for a club. Generated from `name`, so the Clippers get
     * `los-angeles-clippers` rather than the `la-clippers` that `market` would
     * have produced. See `toNbaTeamPageSlug`.
     */
    pageSlug: string;
    logoUrl: string;
    /**
     * The `colorsPalettes.ts` key for this club, e.g. "NBA-BOS".
     *
     * Read off the structured target rather than built as `NBA-${abbreviation}`:
     * the palette table carries legacy aliases (NBA-GS beside NBA-GSW, NBA-LA
     * beside NBA-LAC) and only the target knows which one is current.
     */
    colorPalette: string;
}

export interface NbaTeamDirectoryEntry extends NbaTeamIdentity {
    /** Kalshi's own three-letter code, e.g. "BOS" — the market ticker's suffix. */
    abbreviation: string;
    /**
     * `/nba/teams/boston-celtics` — where every link to a club should point.
     *
     * This used to be `/teams/basketball-team/boston`. Changing it here moves
     * every `/nba/*` page that links a club onto the canonical URL in one edit,
     * rather than leaving two dozen pages pointing at a redirect.
     */
    path: string;
    /** e.g. "Atlantic" today; kept as its own field so headings have one source. */
    divisionLabel: string;
}

const teams = nbaTeamDirectoryJson.teams as Record<string, NbaTeamIdentity>;

/** Every club, alphabetically by full name. */
export const NBA_TEAM_DIRECTORY: NbaTeamDirectoryEntry[] = Object.entries(teams)
    .map(([abbreviation, team]) => ({
        ...team,
        abbreviation,
        path: `/nba/teams/${team.pageSlug}`,
        divisionLabel: team.division,
    }))
    .sort((first, second) => first.name.localeCompare(second.name));

const NBA_TEAM_BY_TARGET_ID = new Map(NBA_TEAM_DIRECTORY.map((team) => [team.id, team]));
const NBA_TEAM_BY_ABBREVIATION = new Map(NBA_TEAM_DIRECTORY.map((team) => [team.abbreviation, team]));
const NBA_TEAM_BY_PAGE_SLUG = new Map(NBA_TEAM_DIRECTORY.map((team) => [team.pageSlug, team]));
const NBA_TEAM_BY_LEGACY_SLUG = new Map(NBA_TEAM_DIRECTORY.map((team) => [team.legacySlug, team]));

/**
 * The club a market resolves for, from the structured-target id in its
 * `custom_strike.basketball_team`.
 *
 * The only join the NBA pages use. Kalshi's own abbreviations appear in market
 * tickers and would be tempting to parse, but a ticker suffix is a naming
 * convention rather than a contract, while the structured-target id is the field
 * the exchange actually publishes the relationship in.
 */
export const findNbaTeamByTargetId = (targetId: string | undefined): NbaTeamDirectoryEntry | undefined =>
    targetId ? NBA_TEAM_BY_TARGET_ID.get(targetId) : undefined;

export const findNbaTeamByAbbreviation = (abbreviation: string | undefined): NbaTeamDirectoryEntry | undefined =>
    abbreviation ? NBA_TEAM_BY_ABBREVIATION.get(abbreviation.trim().toUpperCase()) : undefined;

/**
 * The club a `/nba/teams/[team]` request names, e.g. `boston-celtics`.
 *
 * What makes that route trustworthy where the all-sports name lookup is not: an
 * unknown slug is `undefined` here and a 404 there, with no API call. The name
 * path ends in `selectStructuredTarget`, which returns its best-scoring
 * candidate rather than nothing, so a near miss renders a *different club*.
 */
export const findNbaTeamByPageSlug = (slug: string | undefined): NbaTeamDirectoryEntry | undefined =>
    slug ? NBA_TEAM_BY_PAGE_SLUG.get(slug.trim().toLowerCase()) : undefined;

/**
 * The club a legacy `/teams/basketball-team/[name]` request names, e.g.
 * `bos-celtics` or `la-lakers`.
 *
 * Only used to answer that route with a permanent redirect to `path`. Resolving
 * from the table rather than from the exchange means the redirect costs no
 * request and cannot land on the wrong club.
 *
 * Keyed on `legacySlug` and deliberately **not** on `slug`: the bare-city
 * spellings there belong to college clubs on this same route, so redirecting
 * from them would send a reader looking for the NCAA Memphis Tigers to the
 * Grizzlies and delist a live page.
 */
export const findNbaTeamByLegacySlug = (slug: string | undefined): NbaTeamDirectoryEntry | undefined =>
    slug ? NBA_TEAM_BY_LEGACY_SLUG.get(slug.trim().toLowerCase()) : undefined;

/**
 * How many clubs' hubs to try before giving up on the league-wide read.
 *
 * The lane is queried per club but answers with the whole league, so one club's
 * id fetches all thirty standings rows — and one club whose sweep slot failed to
 * render would otherwise take the entire standings family down with it. Three
 * costs nothing on the happy path, because `fetchNbaHub` stops at the first
 * anchor that answers.
 *
 * Read off the front of the directory, which is sorted by name, so the anchors
 * are the same three on every deployment rather than varying with iteration
 * order.
 */
export const NBA_UPSTREAM_ANCHOR_COUNT = 3;

/**
 * Kalshi's own NBA category hub.
 *
 * Written down once because several pages link to it and each of them guessing is
 * several chances to ship a dead breadcrumb.
 *
 * `nba`, not `pro-basketball-m`. Basketball does not follow baseball's convention
 * here — the exchange's slug for the league really is "nba", where MLB's is
 * "pro-baseball". Three sources agree on it: `public/sitemap-categories.xml`,
 * which is generated from the live category list; `sportsBffParams.vitest.ts`;
 * and `footerMatchupLinks.vitest.ts`.
 *
 * ⚠️ `teams/[type]/[name]/teamSeo.ts` links NBA team pages to
 * `/category/sports/basketball/pro-basketball-m`, which appears in no category
 * listing — that is a pre-existing dead link, and `pro-basketball-m` is a
 * *competition* slug from a different namespace (see the calendar params tests).
 * Not propagated here; fixed separately when the NBA related-links are pointed at
 * these pages.
 */
export const NBA_HUB_PATH = '/category/sports/basketball/nba';

/** Both conferences, East first — the order every page lists them in. */
export const NBA_CONFERENCES: readonly NbaConference[] = ['East', 'West'] as const;

/** The full name of a conference, for prose and headings. */
export const NBA_CONFERENCE_NAMES: Record<NbaConference, string> = {
    East: 'Eastern Conference',
    West: 'Western Conference',
};

/** The URL segment each conference's standings page is served at. */
export const NBA_CONFERENCE_SLUGS: Record<NbaConference, string> = {
    East: 'east',
    West: 'west',
};

/**
 * The three divisions of each conference, in the order the league lists them.
 *
 * Written down rather than discovered from the directory so the sections keep a
 * stable order across renders — a page whose divisions rearrange themselves
 * between two crawls is a different page each time.
 */
export const NBA_DIVISIONS: Record<NbaConference, readonly NbaDivisionName[]> = {
    East: ['Atlantic', 'Central', 'Southeast'],
    West: ['Northwest', 'Pacific', 'Southwest'],
};

/** The clubs of one conference, alphabetically. */
export const nbaTeamsInConference = (conference: NbaConference): NbaTeamDirectoryEntry[] =>
    NBA_TEAM_DIRECTORY.filter((team) => team.conference === conference);

/** How many clubs each conference sends to the postseason proper. */
export const NBA_PLAYOFF_SPOTS_PER_CONFERENCE = 8;

/**
 * How many of those eight are settled before the play-in.
 *
 * Seeds 1–6 qualify directly on record. Seeds 7 and 8 are decided by the play-in
 * tournament among the teams finishing 7th through 10th, which is why this is a
 * separate number from the eight above and why `/nba/play-in/` exists at all.
 */
export const NBA_DIRECT_PLAYOFF_SPOTS_PER_CONFERENCE = 6;

/** How many clubs per conference enter the play-in: the 7th through 10th finishers. */
export const NBA_PLAY_IN_TEAMS_PER_CONFERENCE = 4;

/** The lowest seed the play-in reaches, i.e. 10th in the conference. */
export const NBA_PLAY_IN_LOWEST_SEED = 10;

/** Games in an NBA regular season — the denominator for games-behind arithmetic. */
export const NBA_REGULAR_SEASON_GAMES = 82;
